// 합계 섹션 컴포넌트
import type { OrderData } from './types';
import { receiptTranslations } from './constants';
import { useApp } from '@/contexts/AppContext';
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency';
import { narrowReceiptView } from '@/utils/receipt/narrowReceiptView';

interface TotalSectionProps {
  orderData: OrderData;
}

export function TotalSection({ orderData }: TotalSectionProps) {
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale, currency } = useApp();
  const t = receiptTranslations[locale];
  // #336 C: `??` / `||` fallback 을 narrowReceiptView 1곳에 집중
  // hot-fix #342 P2: pointsUsed 도 view 단일 소스 사용 (boundary 1곳 원칙 준수)
  const view = narrowReceiptView(orderData);

  return (
    <div className="px-5 py-4 space-y-2.5">
      {/* Subtotal (NET) — #299: "NET + IVA = Total" 구조 (narrowReceiptView 가
          netSubtotal 누락 시 GROSS-tax 역산 적용). */}
      <div className="flex justify-between items-center py-1.5">
        <span className="text-sm text-muted-foreground">{t.subtotal}</span>
        <span className="text-sm font-medium text-foreground">
          {formatCurrencyUtil(view.netSubtotal, currency)}
        </span>
      </div>

      {/* 세금 */}
      <div className="flex justify-between items-center py-1.5">
        <span className="text-sm text-muted-foreground">{t.tax}</span>
        <span className="text-sm font-medium text-foreground">
          {formatCurrencyUtil(view.tax, currency)}
        </span>
      </div>

      {/* 팁 */}
      <div className="flex justify-between items-center py-1.5">
        <span className="text-sm text-muted-foreground">{t.tip}</span>
        <span className="text-sm font-medium text-foreground">
          {formatCurrencyUtil(view.tip, currency)}
        </span>
      </div>

      {/* 포인트 사용 — view.pointsUsed 단일 소스 (#342 hot-fix P2) */}
      {view.pointsUsed > 0 && (
        <div className="flex justify-between items-center py-1.5">
          <span className="text-sm font-medium text-secondary">{t.pointsUsed}</span>
          <span className="text-sm font-medium text-secondary">
            -{formatCurrencyUtil(view.pointsUsed, currency)}
          </span>
        </div>
      )}

      {/* 총액 */}
      <div className="pt-3 mt-1 border-t-2 border-border">
        <div className="flex justify-between items-center py-1.5">
          <span className="text-lg font-bold text-secondary">{t.total}</span>
          <span className="text-xl font-bold text-secondary">
            {formatCurrencyUtil(view.total, currency)}
          </span>
        </div>
      </div>

      {/* 결제 방법 — #632 F-19: 미확정('')이면 행 자체를 숨김 ('Card' 선표기 금지) */}
      {view.paymentMethod && (
        <div className="mt-4">
          <div className="flex justify-between items-center px-4 py-3.5 bg-muted rounded-[5px]">
            <span className="text-sm font-medium text-muted-foreground">
              {t.paymentMethod}
            </span>
            <span className="text-sm font-bold text-foreground">
              {view.paymentMethod === 'cash'
                ? t.cashPayment
                : view.paymentMethod === 'transfer'
                ? t.transferPayment
                : t.cardPayment}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
